import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useCart } from '../Context/CartContext'
import { useCurrency } from '../Context/CurrencyContext'
import ProductCard from './ProductCard'
import ProductRail from './ProductRail'
import ProductQuickView from './ProductQuickView'
import CartDrawer from './CartDrawer'
import MainHeader from './MainHeader'
import HomeFooter from './HomeFooter'
import useTemplateScripts from '../hooks/useTemplateScripts'
import { getAllProducts } from '../services/productService'
import { CATEGORY_GROUPS, buildCategoryTree, classifyCategory, collectionPath, detectBrand, isGeneralGroup } from '../constants/shopCategories'
import { CONTACT_INFO, SITE_LINKS } from '../constants/siteLinks'
import './Shop.css'

const PAGE_SIZE = 24

/** Banner photo per category page (falls back to the category's own artwork). */
const SHOP_PERKS = [
  { icon: 'fa-certificate', title: 'Genuine products', text: 'With warranty cover' },
  { icon: 'fa-truck-fast', title: 'Delivery across Rwanda', text: 'Kigali next-day dispatch' },
  { icon: 'fa-screwdriver-wrench', title: 'Professional installation', text: 'By Fabritech engineers' },
  { icon: 'fa-mobile-screen', title: 'Easy payment', text: 'MoMo, Airtel Money or card' },
]

const CATEGORY_BANNERS = {
  starlink: '/assets/img/slider/starlink-daylight-4k.jpg',
  'digital-security': '/assets/img/home/category-digital-security.jpg',
  'power-backup': '/assets/img/home/category-power-backup.jpg',
}
const OTHER_BRAND = 'Other brands'

const SORT_OPTIONS = [
    { id: 'featured', label: 'Featured' },
    { id: 'newest', label: 'Newest' },
    { id: 'price-low', label: 'Price: Low to High' },
    { id: 'price-high', label: 'Price: High to Low' },
    { id: 'discount', label: 'Biggest discount' },
]

const formatRwf = (value) => `RWF ${Math.round(Number(value) || 0).toLocaleString('en-US')}`
const discountOf = (p) => (p.priceOld && p.priceOld > p.price ? (p.priceOld - p.price) / p.priceOld : 0)

/** API product → the shape ProductCard, filters and the quick view use. */
function toShopProduct(product, index) {
    const raw = product.category || product.badge || ''
    const { categoryId, subcategoryId } = classifyCategory(raw, product.title || product.name)
    const price = Number(product.priceNew || product.price) || 0
    const priceOld = Number(product.priceOld) || null
    const brand = product.brand || detectBrand(product.title || product.name, raw)
    return {
        id: product.id || product._id || `product-${index + 1}`,
        order: index,
        name: product.title || product.name || 'Untitled Product',
        title: product.title || product.name || 'Untitled Product',
        category: raw.toLowerCase(),
        categoryId,
        subcategoryId,
        brand: brand === 'Fabritech' ? OTHER_BRAND : brand,
        price,
        priceOld: priceOld && priceOld > price ? priceOld : null,
        image: product.image || '/placeholder-image.png',
        description: product.description || '',
        stock: product.stock ?? null,
        lowStockThreshold: product.lowStockThreshold ?? null,
    }
}

/** Shape expected by ProductQuickView (shared with the home page). */
function toQuickView(product) {
    const pct = Math.round(discountOf(product) * 100)
    return {
        ...product,
        priceNew: formatRwf(product.price),
        priceOld: product.priceOld ? formatRwf(product.priceOld) : null,
        discount: pct > 0 ? `-${pct}%` : null,
        priceNewNum: product.price,
        priceOldNum: product.priceOld,
    }
}

/**
 * Shop: the full catalogue. With no search/category and the default sort, products are grouped into
 * "Explore our … Products" carousels per category; otherwise one grid that loads 24 at a time as you scroll.
 * Sorting and currency apply to both. `?q=` (header search) and `?category=` (header category picker) narrow the list;
 * chips show them and let the shopper clear them.
 *
 * Also serves the category pages (/collections/:slug): `collectionSlug` fixes the category, and the
 * header shows that category's name, description and sub-category pills.
 */
function Shop({ collectionSlug = null }) {
    useTemplateScripts()
    const { cartItems, addToCart } = useCart()
    const { currency, setCurrency, currencies, isForeign, baseCurrency } = useCurrency()
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()

    const [products, setProducts] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [sortBy, setSortBy] = useState('featured')
    const [brand, setBrand] = useState('all')
    const [visible, setVisible] = useState(PAGE_SIZE)
    const [showCart, setShowCart] = useState(false)
    const [quickView, setQuickView] = useState(null)
    const [toast, setToast] = useState(null)
    const [addedId, setAddedId] = useState(null)
    const sentinelRef = useRef(null)
    const subnavRef = useRef(null)

    const searchQuery = (searchParams.get('q') || '').trim()
    const selectedCategory = collectionSlug || searchParams.get('category') || 'all'

    const updateParams = (changes) => {
        const next = new URLSearchParams(searchParams)
        Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value)
            else next.delete(key)
        })
        setSearchParams(next, { replace: true })
    }

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            setLoading(true)
            setError('')
            try {
                const data = await getAllProducts()
                if (!cancelled) setProducts((Array.isArray(data) ? data : []).map(toShopProduct))
            } catch (err) {
                if (!cancelled) {
                    setError(err.message || 'Failed to load products')
                    setProducts([])
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [])

    // The template's main.js turns native <select>s into jQuery widgets React cannot read; undo it for the toolbar.
    useEffect(() => {
        const restore = () => {
            const $ = window.jQuery || window.$
            document.querySelectorAll('.sp-toolbar select').forEach((select) => {
                if ($?.fn?.niceSelect && $(select).next('.nice-select').length) $(select).niceSelect('destroy')
                select.style.display = ''
            })
            document.querySelectorAll('.sp-toolbar .nice-select').forEach((el) => el.remove())
        }
        restore()
        const timer = setInterval(restore, 400)
        const stop = setTimeout(() => clearInterval(timer), 4000)
        return () => {
            clearInterval(timer)
            clearTimeout(stop)
        }
    }, [])

    useEffect(() => {
        if (!toast) return undefined
        const timer = setTimeout(() => setToast(null), 2400)
        return () => clearTimeout(timer)
    }, [toast])

    const categoryTree = useMemo(() => buildCategoryTree(products), [products])
    const matchesSearch = (p) =>
        !searchQuery ||
        `${p.name} ${p.description} ${p.brand} ${p.category}`.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = (p) =>
        selectedCategory === 'all' || p.categoryId === selectedCategory || p.subcategoryId === selectedCategory

    const filtered = useMemo(() => {
        const list = products.filter((p) => matchesSearch(p) && matchesCategory(p) && (brand === 'all' || p.brand === brand))
        const sorters = {
            featured: (a, b) => a.order - b.order,
            newest: (a, b) => String(b.id).localeCompare(String(a.id)),
            'price-low': (a, b) => a.price - b.price,
            'price-high': (a, b) => b.price - a.price,
            discount: (a, b) => discountOf(b) - discountOf(a),
        }
        return list.sort(sorters[sortBy] || sorters.featured)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, searchQuery, selectedCategory, sortBy, brand])

    // Back to the first page whenever the result set changes
    useEffect(() => {
        setVisible(PAGE_SIZE)
    }, [searchQuery, selectedCategory, sortBy, brand])

    // The selected category and, when a sub-category is selected, its parent
    const current = useMemo(() => {
        if (selectedCategory === 'all') return null
        for (const cat of categoryTree) {
            if (cat.id === selectedCategory) return { parent: cat, node: cat }
            const sub = cat.subcategories.find((s) => s.id === selectedCategory)
            if (sub) return { parent: cat, node: sub }
        }
        // A known sub-category with no products yet (e.g. Storage): still show its page
        for (const group of CATEGORY_GROUPS) {
            const sub = group.subcategories.find((s) => s.id === selectedCategory)
            const parent = categoryTree.find((cat) => cat.id === group.id)
            if (sub && parent) return { parent, node: sub }
        }
        return null
    }, [categoryTree, selectedCategory])
    const categoryName = current?.node.name || null
    // Category pages (a category is selected and no search is running) get the banner layout
    const isCategoryPage = Boolean(current) && !searchQuery
    // The shop's front page (no category, no search)
    const isLanding = !current && !searchQuery
    const categoryCount = isCategoryPage ? products.filter((p) => matchesCategory(p)).length : 0

    // Phones: scroll the product-type tiles so the selected one is in view
    useEffect(() => {
        const grid = subnavRef.current
        const active = grid?.querySelector('.is-active')
        if (grid && active && grid.scrollWidth > grid.clientWidth) {
            grid.scrollLeft = active.parentElement.offsetLeft - grid.firstElementChild.offsetLeft
        }
    }, [selectedCategory, products.length])

    // Brands present in this category, most products first; "Other brands" last
    const brandOptions = useMemo(() => {
        const counts = new Map()
        products
            .filter((p) => matchesSearch(p) && matchesCategory(p))
            .forEach((p) => counts.set(p.brand, (counts.get(p.brand) || 0) + 1))
        return [...counts.entries()]
            .sort((a, b) => (a[0] === OTHER_BRAND) - (b[0] === OTHER_BRAND) || b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([name, count]) => ({ name, count }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, searchQuery, selectedCategory])

    // On category pages, changing category opens that category's page; on the shop it updates ?category=
    const goToCategory = (id) => {
        if (collectionSlug) navigate(id ? collectionPath(id) : SITE_LINKS.shop)
        else updateParams({ category: id })
    }

    const activeChips = [
        searchQuery && { key: 'q', label: `“${searchQuery}”`, clear: () => updateParams({ q: null }) },
        categoryName && !collectionSlug && { key: 'cat', label: categoryName, clear: () => goToCategory(null) },
    ].filter(Boolean)

    const clearAll = () => (collectionSlug ? navigate(SITE_LINKS.shop) : updateParams({ q: null, category: null }))

    const handleAdded = (product, quantity = 1) => {
        setAddedId(product.id)
        setToast(quantity > 1 ? `${quantity} × ${product.name} added to cart` : `${product.name} added to cart`)
        setTimeout(() => setAddedId(null), 2000)
    }

    const addFromQuickView = (product, quantity) => {
        addToCart(
            { id: product.id, name: product.name, title: product.title, price: product.price, image: product.image, category: product.category },
            quantity
        )
        handleAdded(product, quantity)
        setQuickView(null)
    }

    const tabs = useMemo(() => {
        if (current) {
            const fixed = CATEGORY_GROUPS.find((group) => group.id === current.parent.id)?.subcategories
            const subs = (fixed || current.parent.subcategories).filter((sub) => !isGeneralGroup(sub))
            if (subs.length < 1) return []
            return [
                { id: 'all', label: `All ${current.parent.name}`, target: current.parent.id, active: current.node === current.parent },
                ...subs.map((sub) => ({
                    id: sub.id,
                    label: sub.name,
                    target: sub.id,
                    active: current.node.id === sub.id,
                })),
            ]
        }
        return [
            { id: 'all', label: 'All Products', target: null, active: true },
            ...categoryTree.map((cat) => ({ id: cat.id, label: cat.name, target: cat.id, active: false })),
        ]
    }, [current, categoryTree])

    const title = searchQuery ? `Results for “${searchQuery}”` : categoryName || 'All Products'
    const grouped = !searchQuery && selectedCategory === 'all' && sortBy === 'featured'
    const groups = useMemo(
        () =>
            categoryTree
                .map((cat) => ({ cat, items: filtered.filter((p) => p.categoryId === cat.id) }))
                .filter((group) => group.items.length > 0),
        [categoryTree, filtered]
    )
    const shown = filtered.slice(0, visible)
    const hasMore = !grouped && shown.length < filtered.length

    // Infinite scroll: load the next page when the sentinel below the grid comes into view
    useEffect(() => {
        const sentinel = sentinelRef.current
        if (!sentinel || !hasMore || typeof IntersectionObserver === 'undefined') return undefined
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) setVisible((v) => v + PAGE_SIZE)
            },
            { rootMargin: '400px 0px' }
        )
        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [hasMore, visible])

    const renderResults = () => {
        if (loading) {
            return (
                <ul className="sp-grid" aria-busy="true" aria-label="Loading products">
                    {Array.from({ length: 8 }, (_, i) => (
                        <li key={i} className="sp-skel" aria-hidden="true">
                            <span className="sp-skel__media" />
                            <span className="sp-skel__line" />
                            <span className="sp-skel__line sp-skel__line--short" />
                            <span className="sp-skel__btn" />
                        </li>
                    ))}
                </ul>
            )
        }
        if (error) {
            return (
                <div className="sp-state">
                    <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                    <h3>We couldn’t load the products</h3>
                    <p>Please check your connection and try again.</p>
                    <button type="button" className="sp-btn sp-btn--primary" onClick={() => window.location.reload()}>
                        Try again
                    </button>
                </div>
            )
        }
        if (filtered.length === 0) {
            return (
                <div className="sp-state">
                    <i className={`fa-solid ${isCategoryPage ? current.node.icon || 'fa-box-open' : 'fa-magnifying-glass'}`} aria-hidden="true" />
                    <h3>{isCategoryPage ? `${current.node.name} — available on request` : 'No products found'}</h3>
                    <p>
                        {isCategoryPage
                            ? 'These products aren’t listed online yet. Ask our team for models, prices and availability.'
                            : 'Try a different search, or ask us — we can source equipment that isn’t listed yet.'}
                    </p>
                    <div className="sp-state__actions">
                        {isCategoryPage && current.node !== current.parent ? (
                            <button type="button" className="sp-btn sp-btn--primary" onClick={() => goToCategory(current.parent.id)}>
                                All {current.parent.name}
                            </button>
                        ) : (
                            <button type="button" className="sp-btn sp-btn--primary" onClick={clearAll}>
                                Show all products
                            </button>
                        )}
                        <a href={SITE_LINKS.contact} className="sp-btn sp-btn--outline">
                            Ask our team
                        </a>
                    </div>
                </div>
            )
        }
        const renderGrid = (items) => (
            <ul className="sp-grid">
                {items.map((product) => (
                    <li key={product.id}>
                        <ProductCard
                            product={product}
                            addedProduct={addedId}
                            onAdded={handleAdded}
                            onQuickView={(p) => setQuickView(toQuickView(p))}
                        />
                    </li>
                ))}
            </ul>
        )

        if (grouped) {
            return groups.map(({ cat, items }) => (
                <ProductRail
                    key={cat.id}
                    id={cat.id}
                    title={`Explore our ${cat.name}${/equipment$/i.test(cat.name) ? "" : " Products"}`}
                >
                    {items.map((product) => (
                        <li key={product.id}>
                            <ProductCard
                                product={product}
                                addedProduct={addedId}
                                onAdded={handleAdded}
                                onQuickView={(p) => setQuickView(toQuickView(p))}
                            />
                        </li>
                    ))}
                </ProductRail>
            ))
        }

        return (
            <>
                {renderGrid(shown)}
                {hasMore ? (
                    <div ref={sentinelRef} className="sp-more" role="status">
                        <span className="sp-spinner" aria-hidden="true" />
                        <span className="sp-visually-hidden">Loading more products</span>
                    </div>
                ) : (
                    filtered.length > PAGE_SIZE && (
                        <p className="sp-end">You’ve seen all {filtered.length} products</p>
                    )
                )}
            </>
        )
    }

    return (
        <div className="sp-page">
            <MainHeader cartCount={cartItems.length} onCartClick={() => setShowCart((open) => !open)} />

            <main>
                {isCategoryPage ? (
                    <>
                        <section className="sp-cat">
                            <div className="sp-container">
                                <div className="sp-cat__banner">
                                    <div className="sp-cat__copy">
                                        <nav className="sp-crumbs" aria-label="Breadcrumb">
                                            <Link to={SITE_LINKS.home}>Home</Link>
                                            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                                            <Link to={SITE_LINKS.shop}>Shop</Link>
                                            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                                            {current.node !== current.parent && (
                                                <>
                                                    <Link to={collectionPath(current.parent.id)}>{current.parent.name}</Link>
                                                    <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                                                </>
                                            )}
                                            <span aria-current="page">{current.node.name}</span>
                                        </nav>
                                        <span className="sp-cat__icon" aria-hidden="true">
                                            <i className={`fa-solid ${current.node.icon || current.parent.icon || 'fa-box'}`} />
                                        </span>
                                        <h1 className="sp-cat__title">{current.node.name}</h1>
                                        <p className="sp-cat__text">
                                            {current.node.description}. Genuine products with warranty, delivered across Rwanda and
                                            installed by our engineers.
                                        </p>
                                        <div className="sp-cat__meta">
                                            {categoryCount > 0 && (
                                                <span>
                                                    <i className="fa-solid fa-box-open" aria-hidden="true" />
                                                    {categoryCount} {categoryCount === 1 ? 'product' : 'products'}
                                                </span>
                                            )}
                                            {brandOptions.filter((b) => b.name !== OTHER_BRAND).length > 0 && (
                                                <span>
                                                    <i className="fa-solid fa-tag" aria-hidden="true" />
                                                    {brandOptions
                                                        .filter((b) => b.name !== OTHER_BRAND)
                                                        .map((b) => b.name)
                                                        .join(' · ')}
                                                </span>
                                            )}
                                        </div>
                                        <div className="sp-cat__actions">
                                            <a href={SITE_LINKS.contact} className="sp-btn sp-btn--primary">
                                                Request a Quote
                                                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                                            </a>
                                            <a href={CONTACT_INFO.phoneHref} className="sp-btn sp-btn--outline">
                                                <i className="fa-solid fa-phone" aria-hidden="true" />
                                                {CONTACT_INFO.phone}
                                            </a>
                                        </div>
                                    </div>
                                    <div className="sp-cat__art" aria-hidden="true">
                                        <img src={CATEGORY_BANNERS[current.parent.id] || current.node.image || current.parent.image} alt="" />
                                    </div>
                                </div>
                                {tabs.length > 1 && (
                                    <nav className="sp-subnav" aria-label={`${current.parent.name} categories`}>
                                        <div className="sp-subnav__head">
                                            <h2 className="sp-subnav__title">Browse {current.parent.name}</h2>
                                            <span className="sp-subnav__hint">Choose a product type</span>
                                        </div>
                                        <ul className="sp-subnav__grid" ref={subnavRef}>
                                            {tabs.map((tab) => {
                                                const sub = CATEGORY_GROUPS.flatMap((group) => group.subcategories).find((item) => item.id === tab.id)
                                                const count = products.filter((p) =>
                                                    tab.id === 'all' ? p.categoryId === current.parent.id : p.subcategoryId === tab.id
                                                ).length
                                                return (
                                                    <li key={tab.id}>
                                                        <button
                                                            type="button"
                                                            className={`sp-subnav__item${tab.active ? ' is-active' : ''}`}
                                                            aria-current={tab.active ? 'page' : undefined}
                                                            onClick={() => goToCategory(tab.target)}
                                                        >
                                                            <span className="sp-subnav__icon" aria-hidden="true">
                                                                <i className={`fa-solid ${sub?.icon || 'fa-border-all'}`} />
                                                            </span>
                                                            <span className="sp-subnav__name">{tab.id === 'all' ? `All ${current.parent.name}` : sub?.name || tab.label}</span>
                                                            <span className="sp-subnav__count">{count > 0 ? `${count} ${count === 1 ? 'product' : 'products'}` : 'On request'}</span>
                                                        </button>
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                    </nav>
                                )}
                            </div>
                        </section>
                    </>
                ) : isLanding ? (
                    <section className="sp-cat">
                        <div className="sp-container">
                            <div className="sp-landing">
                                <span className="sp-landing__bg" aria-hidden="true" style={{ backgroundImage: "url('/assets/img/slider/starlink-hero-sky.png')" }} />
                                <div className="sp-landing__copy">
                                    <nav className="sp-crumbs" aria-label="Breadcrumb">
                                        <Link to={SITE_LINKS.home}>Home</Link>
                                        <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                                        <span aria-current="page">Shop</span>
                                    </nav>
                                    <span className="sp-landing__kicker">Fabritech Store</span>
                                    <h1 className="sp-landing__title">
                                        Technology You Can <span className="fab-gradient-text">Trust</span>
                                    </h1>
                                    <p className="sp-landing__text">
                                        Genuine Starlink kits, CCTV and security systems, and power backup — delivered across
                                        Rwanda and installed by our engineers.
                                    </p>
                                    <div className="sp-landing__cats">
                                        {categoryTree.map((cat, i) => (
                                            <Link
                                                key={cat.id}
                                                to={collectionPath(cat.id)}
                                                className={`sp-btn ${i === 0 ? 'sp-btn--primary' : 'sp-btn--outline'}`}
                                            >
                                                <i className={`fa-solid ${cat.icon}`} aria-hidden="true" />
                                                {cat.name}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                                <ul className="sp-landing__perks">
                                    {SHOP_PERKS.map((perk) => (
                                        <li key={perk.title}>
                                            <span className="sp-landing__perk-icon" aria-hidden="true">
                                                <i className={`fa-solid ${perk.icon}`} />
                                            </span>
                                            <span>
                                                <strong>{perk.title}</strong>
                                                {perk.text}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>
                ) : (
                    <section className="sp-hero">
                        <div className="sp-container">
                            <div className="sp-hero__row">
                                <div className="sp-hero__copy">
                                    <nav className="sp-crumbs" aria-label="Breadcrumb">
                                        <Link to={SITE_LINKS.home}>Home</Link>
                                        <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                                        {current ? <Link to={SITE_LINKS.shop}>Shop</Link> : <span aria-current="page">Shop</span>}
                                        {current && current.node !== current.parent && (
                                            <>
                                                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                                                <button type="button" className="sp-crumbs__link" onClick={() => goToCategory(current.parent.id)}>
                                                    {current.parent.name}
                                                </button>
                                            </>
                                        )}
                                        {current && (
                                            <>
                                                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                                                <span aria-current="page">{current.node.name}</span>
                                            </>
                                        )}
                                    </nav>
                                    <h1 className="sp-hero__title">{title}</h1>
                                    <p className="sp-hero__meta">
                                        {current && !searchQuery && current.node.description
                                            ? current.node.description
                                            : 'Starlink kits, CCTV systems, networking and power equipment'}
                                    </p>
                                </div>

                                <a href={CONTACT_INFO.phoneHref} className="sp-advice">
                                    <span className="sp-advice__icon" aria-hidden="true">
                                        <i className="fa-solid fa-headset" />
                                    </span>
                                    <span className="sp-advice__text">
                                        <span>Need help choosing?</span>
                                        <strong>Talk to an expert · {CONTACT_INFO.phone}</strong>
                                    </span>
                                    <i className="fa-solid fa-arrow-right sp-advice__go" aria-hidden="true" />
                                </a>
                            </div>

                            {tabs.length > 1 && (
                                <nav className="sp-tabs" aria-label="Categories">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            className={`sp-tabs__tab${tab.active ? ' is-active' : ''}`}
                                            aria-current={tab.active ? 'page' : undefined}
                                            onClick={() => goToCategory(tab.target)}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </nav>
                            )}
                        </div>
                    </section>
                )}

                <div className="sp-container sp-layout">
                    <div className="sp-main">
                        {isLanding && categoryTree.length > 0 && (
                            <section className="sp-cats" aria-labelledby="sp-cats-title">
                                <h2 id="sp-cats-title" className="sp-cats__title">Shop by category</h2>
                                <ul className="sp-cats__grid">
                                    {categoryTree.map((cat) => (
                                        <li key={cat.id}>
                                            <Link to={collectionPath(cat.id)} className="sp-catcard">
                                                <span
                                                    className="sp-catcard__img"
                                                    style={{ backgroundImage: `url('${CATEGORY_BANNERS[cat.id] || cat.image || cat.productImage}')` }}
                                                />
                                                <span className="sp-catcard__body">
                                                    <span className="sp-catcard__icon" aria-hidden="true">
                                                        <i className={`fa-solid ${cat.icon}`} />
                                                    </span>
                                                    <span className="sp-catcard__text">
                                                        <strong>{cat.name}</strong>
                                                        <span>{cat.description}</span>
                                                    </span>
                                                </span>
                                                <span className="sp-catcard__cta">
                                                    Shop now
                                                    <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}
                        {isCategoryPage && brandOptions.length > 1 && (
                            <div className="sp-brands" role="group" aria-label="Filter by brand">
                                <span className="sp-brands__label">Brand</span>
                                {[{ name: 'all', label: 'All brands', count: brandOptions.reduce((n, b) => n + b.count, 0) }, ...brandOptions].map((b) => (
                                    <button
                                        key={b.name}
                                        type="button"
                                        className={`sp-brands__chip${brand === b.name ? ' is-active' : ''}`}
                                        aria-pressed={brand === b.name}
                                        onClick={() => setBrand(b.name)}
                                    >
                                        {b.label || b.name}
                                        <em>{b.count}</em>
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="sp-toolbar">
                            <p className="sp-toolbar__count">
                                {loading
                                    ? 'Loading products…'
                                    : filtered.length === 0
                                      ? '0 products'
                                      : grouped
                                        ? `${filtered.length} products in ${groups.length} categor${groups.length === 1 ? 'y' : 'ies'}`
                                        : `Showing 1–${shown.length} of ${filtered.length} product${filtered.length === 1 ? '' : 's'}`}
                            </p>
                            <div className="sp-toolbar__controls">
                                <label className="sp-select">
                                    <span>Currency</span>
                                    <select value={currency} onChange={(e) => setCurrency(e.target.value)} aria-label="Display currency">
                                        {currencies.map((c) => (
                                            <option key={c.code} value={c.code}>
                                                {c.code}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="sp-select">
                                    <span>Sort by</span>
                                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort products">
                                        {SORT_OPTIONS.map((option) => (
                                            <option key={option.id} value={option.id}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>

                        {activeChips.length > 0 && (
                            <div className="sp-chips">
                                {activeChips.map((chip) => (
                                    <button key={chip.key} type="button" className="sp-chip" onClick={chip.clear}>
                                        {chip.label}
                                        <i className="fa-solid fa-xmark" aria-label="Remove filter" />
                                    </button>
                                ))}
                                <button type="button" className="sp-link" onClick={clearAll}>
                                    Clear all
                                </button>
                            </div>
                        )}

                        {isForeign && (
                            <p className="sp-note" role="note">
                                <i className="fa-solid fa-circle-info" aria-hidden="true" />
                                Prices shown in <strong>{currency}</strong> for reference. Checkout and payment are in{' '}
                                <strong>{baseCurrency}</strong>.
                            </p>
                        )}

                        {renderResults()}
                    </div>
                </div>

                <section className="sp-container sp-help">
                    <div className="sp-help__icon" aria-hidden="true">
                        <i className="fa-solid fa-headset" />
                    </div>
                    <div className="sp-help__text">
                        <h2>Not sure what you need?</h2>
                        <p>Tell us about your site and our engineers will recommend the right equipment and installation.</p>
                    </div>
                    <div className="sp-help__actions">
                        <a href={CONTACT_INFO.phoneHref} className="sp-btn sp-btn--outline">
                            <i className="fa-solid fa-phone" aria-hidden="true" />
                            {CONTACT_INFO.phone}
                        </a>
                        <a href={SITE_LINKS.contact} className="sp-btn sp-btn--primary">
                            Request a quote
                            <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                        </a>
                    </div>
                </section>
            </main>

            <HomeFooter />

            <CartDrawer open={showCart} onClose={() => setShowCart(false)} onToast={(message) => setToast(message)} />

            {quickView && (
                <ProductQuickView product={quickView} onClose={() => setQuickView(null)} onAddToCart={addFromQuickView} />
            )}

            {toast && (
                <div className="sp-toast" role="status">
                    <i className="fa-solid fa-circle-check" aria-hidden="true" />
                    {toast}
                </div>
            )}
        </div>
    )
}

export default Shop
